import { StateCreator } from 'zustand';
import type { AppState } from '../../store/app-state';
import { logger } from '../../utils/logger.ts';
import { CueAnchor, CustomCue, PromptBookFilters, ViewDensity, TEXT_SCALE_STOPS, TEXT_SCALE_DEFAULT } from './types';
import { getCueSourceAdapter } from './cue-source-registry.ts';
import { deleteCueAnchor } from './prompt-book-api.ts';
import type { SupportedPromptBookCueType } from './cue-creation.ts';
import type { ProjectScope } from '../sync/project-scope.ts';
import {
    markPendingCueAnchorDelete,
    markPendingCueAnchorWrite,
    markPendingCustomCueWrite,
} from '../sync/prompt-book-pending-writes';

export interface PromptBookSlice {
    cueAnchors: CueAnchor[];
    customCues: CustomCue[];
    promptBookUI: {
        filters: PromptBookFilters;
        viewDensity: ViewDensity;
        textScale: number; // 0.5–2.0 in 0.25 steps
    };
    promptBookActions: {
        // Cue Anchors
        addCueAnchor: (anchor: Partial<CueAnchor>) => string;
        removeCueAnchor: (id: string) => void;
        updateCueAnchor: (id: string, updates: Partial<CueAnchor>) => void;
        bulkUpdateAnchors: (updates: { id: string; updates: Partial<CueAnchor> }[]) => void;
        createSourceCueAndAnchor: (input: {
            cueType: SupportedPromptBookCueType;
            cueNumber: string;
            notes: string;
            sceneId: string;
            afterSourceId?: string;
            position: Pick<CueAnchor, 'blockId' | 'charOffset' | 'contextSnippet'>;
        }) => { sourceId: string; anchorId: string };
        updateSourceCue: (input: {
            cueType: SupportedPromptBookCueType;
            sourceId: string;
            cueNumber: string;
            notes: string;
        }) => void;

        // Custom Cues
        addCustomCue: (cue: Partial<CustomCue>) => string;
        updateCustomCue: (id: string, updates: Partial<CustomCue>) => void;
        deleteCustomCue: (id: string) => void;

        // UI state
        setFilter: (dept: keyof PromptBookFilters, visible: boolean) => void;
        toggleFilter: (dept: keyof PromptBookFilters) => void;
        setViewDensity: (density: ViewDensity) => void;
        increaseTextScale: () => void;
        decreaseTextScale: () => void;
        resetTextScale: () => void;

        // Anchor resilience
        removeAnchorsForBlock: (blockId: string) => void;
        migrateAnchorsOnSplit: (oldBlockId: string, newBlockId: string, splitOffset: number) => void;
        migrateAnchorsOnMerge: (absorbedBlockId: string, targetBlockId: string, prependLength: number) => void;

        // Project scoping
        clearPromptBook: () => void;
    };
}

function getPromptBookProjectScope(state: AppState): ProjectScope | null {
    if (!state.cloud.enabled || !state.cloud.orgId || !state.cloud.projectId) {
        return null;
    }
    return { orgId: state.cloud.orgId, projectId: state.cloud.projectId };
}

function preserveCueAnchorDelete(operation: Promise<void>): void {
    void operation.catch(error => {
        logger.error('[prompt-book-store] Failed to preserve cue anchor deletion:', error);
    });
}

export const createPromptBookSlice: StateCreator<AppState, [['zustand/immer', never]], [], PromptBookSlice> = (set, get) => ({
    cueAnchors: [],
    customCues: [],
    promptBookUI: {
        filters: { lx: true, sq: true, fs: true, deck: true, fly: true, av: true },
        viewDensity: 'compact',
        textScale: TEXT_SCALE_DEFAULT,
    },
    promptBookActions: {
        // ── Cue Anchors ────────────────────────────────────────────
        addCueAnchor: (anchor) => {
            const id = crypto.randomUUID();
            set(state => {
                state.cueAnchors.push({
                    id,
                    cueType: anchor.cueType || 'lx',
                    sourceId: anchor.sourceId || '',
                    blockId: anchor.blockId || '',
                    charOffset: anchor.charOffset ?? 0,
                    contextSnippet: anchor.contextSnippet || '',
                    sortOrder: anchor.sortOrder ?? state.cueAnchors.length,
                });
            });
            const scope = getPromptBookProjectScope(get());
            if (scope) {
                markPendingCueAnchorWrite(scope, id);
            }
            return id;
        },
        removeCueAnchor: (id) => {
            let didRemove = false;
            set(state => {
                didRemove = state.cueAnchors.some(a => a.id === id);
                state.cueAnchors = state.cueAnchors.filter(a => a.id !== id);
            });
            const scope = getPromptBookProjectScope(get());
            if (scope && didRemove) {
                markPendingCueAnchorDelete(scope, id);
                preserveCueAnchorDelete(deleteCueAnchor(scope, id));
            }
        },
        updateCueAnchor: (id, updates) => {
            let didUpdate = false;
            set(state => {
                const idx = state.cueAnchors.findIndex(a => a.id === id);
                if (idx !== -1) {
                    Object.assign(state.cueAnchors[idx]!, updates);
                    didUpdate = true;
                }
            });
            const scope = getPromptBookProjectScope(get());
            if (scope && didUpdate) {
                markPendingCueAnchorWrite(scope, id);
            }
        },
        bulkUpdateAnchors: (updates) => {
            const updatedAnchorIds: string[] = [];
            set(state => {
                for (const { id, updates: u } of updates) {
                    const idx = state.cueAnchors.findIndex(a => a.id === id);
                    if (idx !== -1) {
                        Object.assign(state.cueAnchors[idx]!, u);
                        updatedAnchorIds.push(id);
                    }
                }
            });
            const scope = getPromptBookProjectScope(get());
            if (scope) {
                for (const id of updatedAnchorIds) {
                    markPendingCueAnchorWrite(scope, id);
                }
            }
        },
        createSourceCueAndAnchor: (input) => {
            const cueNumber = input.cueNumber.trim();
            if (!cueNumber) throw new Error('Cue number is required');

            const sourceId = getCueSourceAdapter(input.cueType).create(get(), {
                cueNumber,
                notes: input.notes,
                sceneId: input.sceneId,
                afterSourceId: input.afterSourceId,
            });
            const anchorId = get().promptBookActions.addCueAnchor({
                ...input.position,
                cueType: input.cueType,
                sourceId,
            });

            return { sourceId, anchorId };
        },
        updateSourceCue: (input) => {
            const cueNumber = input.cueNumber.trim();
            if (!cueNumber) throw new Error('Cue number is required');

            getCueSourceAdapter(input.cueType).update(get(), input.sourceId, {
                cueNumber,
                notes: input.notes,
            });
        },

        // ── Custom Cues ────────────────────────────────────────────
        addCustomCue: (cue) => {
            const id = crypto.randomUUID();
            set(state => {
                state.customCues.push({
                    id,
                    type: cue.type || 'note',
                    label: cue.label || '',
                    linkedCueIds: cue.linkedCueIds,
                    blockId: cue.blockId || '',
                    charOffset: cue.charOffset ?? 0,
                    contextSnippet: cue.contextSnippet || '',
                    sortOrder: cue.sortOrder ?? state.customCues.length,
                });
            });
            const scope = getPromptBookProjectScope(get());
            if (scope) {
                markPendingCustomCueWrite(scope, id);
            }
            return id;
        },
        updateCustomCue: (id, updates) => {
            let didUpdate = false;
            set(state => {
                const idx = state.customCues.findIndex(c => c.id === id);
                if (idx !== -1) {
                    Object.assign(state.customCues[idx]!, updates);
                    didUpdate = true;
                }
            });
            const scope = getPromptBookProjectScope(get());
            if (scope && didUpdate) {
                markPendingCustomCueWrite(scope, id);
            }
        },
        deleteCustomCue: (id) => {
            let didDelete = false;
            set(state => {
                const idx = state.customCues.findIndex(c => c.id === id);
                if (idx !== -1) {
                    state.customCues[idx]!.deleted = true;
                    state.customCues[idx]!.deletedAt = Date.now();
                    didDelete = true;
                }
            });
            const scope = getPromptBookProjectScope(get());
            if (scope && didDelete) {
                markPendingCustomCueWrite(scope, id);
            }
        },

        // ── UI State ───────────────────────────────────────────────
        setFilter: (dept, visible) => {
            set(state => { state.promptBookUI.filters[dept] = visible; });
        },
        toggleFilter: (dept) => {
            set(state => { state.promptBookUI.filters[dept] = !state.promptBookUI.filters[dept]; });
        },
        setViewDensity: (density) => {
            set(state => { state.promptBookUI.viewDensity = density; });
        },
        increaseTextScale: () => {
            set(state => {
                const currentIdx = TEXT_SCALE_STOPS.indexOf(state.promptBookUI.textScale as typeof TEXT_SCALE_STOPS[number]);
                const nextIdx = currentIdx === -1
                    ? TEXT_SCALE_STOPS.indexOf(TEXT_SCALE_DEFAULT)
                    : Math.min(currentIdx + 1, TEXT_SCALE_STOPS.length - 1);
                state.promptBookUI.textScale = TEXT_SCALE_STOPS[nextIdx]!;
            });
        },
        decreaseTextScale: () => {
            set(state => {
                const currentIdx = TEXT_SCALE_STOPS.indexOf(state.promptBookUI.textScale as typeof TEXT_SCALE_STOPS[number]);
                const nextIdx = currentIdx === -1
                    ? TEXT_SCALE_STOPS.indexOf(TEXT_SCALE_DEFAULT)
                    : Math.max(currentIdx - 1, 0);
                state.promptBookUI.textScale = TEXT_SCALE_STOPS[nextIdx]!;
            });
        },
        resetTextScale: () => {
            set(state => { state.promptBookUI.textScale = TEXT_SCALE_DEFAULT; });
        },

        // ── Anchor Resilience ──────────────────────────────────────
        removeAnchorsForBlock: (blockId) => {
            const removedAnchorIds: string[] = [];
            const deletedCustomCueIds: string[] = [];
            set(state => {
                removedAnchorIds.push(...state.cueAnchors
                    .filter(a => a.blockId === blockId)
                    .map(a => a.id));
                state.cueAnchors = state.cueAnchors.filter(a => a.blockId !== blockId);
                // Flag custom cues as orphaned (soft-delete) rather than removing
                for (const c of state.customCues) {
                    if (c.blockId === blockId && !c.deleted) {
                        c.deleted = true;
                        c.deletedAt = Date.now();
                        deletedCustomCueIds.push(c.id);
                    }
                }
            });
            const scope = getPromptBookProjectScope(get());
            if (scope) {
                for (const id of removedAnchorIds) {
                    markPendingCueAnchorDelete(scope, id);
                    preserveCueAnchorDelete(deleteCueAnchor(scope, id));
                }
                for (const id of deletedCustomCueIds) {
                    markPendingCustomCueWrite(scope, id);
                }
            }
        },
        migrateAnchorsOnSplit: (oldBlockId, newBlockId, splitOffset) => {
            const movedAnchorIds: string[] = [];
            const movedCustomCueIds: string[] = [];
            set(state => {
                // Move anchors past the split point to the new block
                for (const a of state.cueAnchors) {
                    if (a.blockId === oldBlockId && a.charOffset >= splitOffset) {
                        a.blockId = newBlockId;
                        a.charOffset -= splitOffset;
                        movedAnchorIds.push(a.id);
                    }
                }
                for (const c of state.customCues) {
                    if (c.blockId === oldBlockId && c.charOffset >= splitOffset && !c.deleted) {
                        c.blockId = newBlockId;
                        c.charOffset -= splitOffset;
                        movedCustomCueIds.push(c.id);
                    }
                }
            });
            const scope = getPromptBookProjectScope(get());
            if (scope) {
                for (const id of movedAnchorIds) {
                    markPendingCueAnchorWrite(scope, id);
                }
                for (const id of movedCustomCueIds) {
                    markPendingCustomCueWrite(scope, id);
                }
            }
        },
        migrateAnchorsOnMerge: (absorbedBlockId, targetBlockId, prependLength) => {
            const movedAnchorIds: string[] = [];
            const movedCustomCueIds: string[] = [];
            set(state => {
                // Move anchors from absorbed block to target with adjusted offset
                for (const a of state.cueAnchors) {
                    if (a.blockId === absorbedBlockId) {
                        a.blockId = targetBlockId;
                        a.charOffset += prependLength;
                        movedAnchorIds.push(a.id);
                    }
                }
                for (const c of state.customCues) {
                    if (c.blockId === absorbedBlockId && !c.deleted) {
                        c.blockId = targetBlockId;
                        c.charOffset += prependLength;
                        movedCustomCueIds.push(c.id);
                    }
                }
            });
            const scope = getPromptBookProjectScope(get());
            if (scope) {
                for (const id of movedAnchorIds) {
                    markPendingCueAnchorWrite(scope, id);
                }
                for (const id of movedCustomCueIds) {
                    markPendingCustomCueWrite(scope, id);
                }
            }
        },

        // ── Clear ──────────────────────────────────────────────────
        clearPromptBook: () => {
            set(state => {
                state.cueAnchors = [];
                state.customCues = [];
            });
        },
    },
});
